import { RFQ_WORKFLOW_STEPS, STATUS_LABELS } from "@/lib/utils";
import { cn } from "@/lib/utils";

type StepState = "complete" | "current" | "upcoming";

function stepState(stepKey: string, status: string): StepState {
  if (status === "cancelled") return "upcoming";

  const currentIndex = RFQ_WORKFLOW_STEPS.findIndex((s) => s.key === status);
  const stepIndex = RFQ_WORKFLOW_STEPS.findIndex((s) => s.key === stepKey);
  if (currentIndex === -1 || stepIndex === -1) return "upcoming";
  if (stepIndex < currentIndex) return "complete";
  if (stepIndex === currentIndex) return "current";
  return "upcoming";
}

function connectorState(afterStepKey: string, status: string): "complete" | "upcoming" {
  if (status === "cancelled") return "upcoming";
  const currentIndex = RFQ_WORKFLOW_STEPS.findIndex((s) => s.key === status);
  const stepIndex = RFQ_WORKFLOW_STEPS.findIndex((s) => s.key === afterStepKey);
  return stepIndex < currentIndex ? "complete" : "upcoming";
}

export function RfqWorkflowStepper({ status }: { status: string }) {
  const isCancelled = status === "cancelled";

  return (
    <div className="space-y-5">
      {isCancelled && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700">
          This RFQ was cancelled
        </div>
      )}

      <div className="w-full overflow-x-auto">
        <ol className="flex w-full min-w-[720px] items-start justify-between gap-0">
          {RFQ_WORKFLOW_STEPS.map((step, index) => {
            const state = stepState(step.key, status);
            const isLast = index === RFQ_WORKFLOW_STEPS.length - 1;

            return (
              <li key={step.key} className="flex flex-1 items-start">
                <div className="flex w-full flex-col items-center">
                  <div className="flex w-full items-center">
                    <div
                      className={cn(
                        "relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold",
                        state === "current" &&
                          "border-argon-primary bg-argon-primary text-white ring-4 ring-argon-primary/20",
                        state === "complete" && "border-argon-success bg-argon-success text-white",
                        state === "upcoming" && "border-gray-200 bg-gray-50 text-gray-400",
                        isCancelled && state === "upcoming" && "opacity-50",
                      )}
                      aria-current={state === "current" ? "step" : undefined}
                    >
                      {state === "complete" ? (
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </div>

                    {!isLast && (
                      <div
                        className={cn(
                          "mx-2 h-1 flex-1 rounded-full",
                          connectorState(step.key, status) === "complete"
                            ? "bg-argon-success"
                            : "bg-gray-200",
                        )}
                        aria-hidden
                      />
                    )}
                  </div>

                  <div className="mt-3 w-full max-w-[6.5rem] text-center">
                    <p
                      className={cn(
                        "text-xs font-bold leading-snug",
                        state === "current" && "text-argon-primary",
                        state === "complete" && "text-argon-dark",
                        state === "upcoming" && "text-gray-400",
                      )}
                    >
                      {step.label}
                    </p>
                    {state === "current" && !isCancelled && (
                      <span className="mt-1 inline-block rounded-full bg-argon-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-argon-primary">
                        You are here
                      </span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      <p className="text-sm text-argon-secondary">
        Current status:{" "}
        <span className="font-semibold text-argon-dark">{STATUS_LABELS[status] ?? status}</span>
      </p>
    </div>
  );
}
